// Rollfristående läge: välj egen roll och vilka roller som använder systemet.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PROJECT_ROLES, type ProjectRole } from "@/lib/basProcess/roleViews";
import { parseRolesInSystem, isProjectRole } from "@/lib/basProcess/soloMode";
import { UserCog } from "lucide-react";

interface Props {
  projectId: string;
  canEdit: boolean;
  onSaved?: () => void;
}

export const ProjectRoleSetup: React.FC<Props> = ({ projectId, canEdit, onSaved }) => {
  const [ownRole, setOwnRole] = useState<ProjectRole>("Byggherre");
  const [inSystem, setInSystem] = useState<ProjectRole[]>([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("projects")
      .select("owner_role, roles_in_system")
      .eq("id", projectId)
      .maybeSingle();
    const r = (data as { owner_role?: string | null } | null)?.owner_role;
    if (isProjectRole(r)) setOwnRole(r);
    setInSystem(parseRolesInSystem((data as { roles_in_system?: unknown } | null)?.roles_in_system));
    setLoaded(true);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("projects")
      .update({ owner_role: ownRole, roles_in_system: inSystem })
      .eq("id", projectId);
    setSaving(false);
    if (error) {
      toast.error("Kunde inte spara rollinställningen");
      return;
    }
    toast.success("Rollinställningen sparad");
    onSaved?.();
  };

  const toggle = (role: ProjectRole) =>
    setInSystem((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  if (!loaded) return null;

  const outside = PROJECT_ROLES.filter((r) => r !== ownRole && !inSystem.includes(r));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCog className="h-4 w-4" /> Din roll och vilka som är med
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <p className="text-sm font-medium">Vilken roll har du i projektet?</p>
          <Select
            value={ownRole}
            onValueChange={(v) => setOwnRole(v as ProjectRole)}
            disabled={!canEdit}
          >
            <SelectTrigger className="w-full sm:w-[240px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROJECT_ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Vilka andra roller arbetar i systemet?</p>
          <p className="text-xs text-muted-foreground">
            Kryssa i de som använder systemet. Roller du inte kryssar i sköts utanför – då kan du
            själv registrera vad de har levererat, i stället för att vänta på dem.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {PROJECT_ROLES.filter((r) => r !== ownRole).map((r) => (
              <label key={r} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={inSystem.includes(r)}
                  onCheckedChange={() => toggle(r)}
                  disabled={!canEdit}
                />
                {r}
              </label>
            ))}
          </div>
        </div>

        {outside.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Utanför systemet:</span>
            {outside.map((r) => (
              <Badge key={r} variant="outline" className="text-xs">
                {r}
              </Badge>
            ))}
          </div>
        )}

        {canEdit && (
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? "Sparar…" : "Spara"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectRoleSetup;
